import {readCookie} from '@Utils/cookie.util';

/**
 * Mirrors SplitTest::WINDOWED_SCORE_GALLERY_AB_TEST_ID. Both halves of a JS split test have to name
 * the experiment — the backend to key the cookie it writes, the frontend to read that cookie back.
 */
const WINDOWED_SCORE_GALLERY_AB_TEST_ID = 'e21f6a0c4e6a4b6a9d3f2b7c1a5e8d94';

const SPLIT_TEST_COOKIE_PREFIX = 'evk_';

/**
 * The split-test infrastructure numbers arms from 0, and GTM reads the raw cookie value, so the arm
 * numbers rather than descriptive names are what analytics sees.
 */
const SPLIT_TEST_ARM_CONTROL = '0';
const SPLIT_TEST_ARM_WINDOWED = '1';

const GALLERY_SPLIT_TEST_TEMPLATE_CLICK_GA4_EVENT_NAME = 'DesignGallerySplitTest_TemplateClicked';

/**
 * Mirrors SplitTestCategorySlug on the backend. The arm cookie is assigned on these five categories
 * but is then sent on every request site-wide for a fortnight, so "the visitor has an arm" is not the
 * same question as "this page is in the experiment" — only the category answers that.
 */
const TEST_CATEGORY_SLUGS: readonly string[] = [
  'birthday-posters',
  'church-flyers',
  'halloween-flyers',
  'bar-flyers',
  'small-business-flyers',
];

/** Mirrors Poster::clickedFromGallery() and the `categorySlug` POST param it reads. */
const TEMPLATE_CLICK_ENDPOINT = 'poster/clickedFromGallery';
const TEMPLATE_CLICK_CATEGORY_SLUG_PARAM = 'categorySlug';

/**
 * Reads the arm assigned by the split-test infrastructure, which writes `evk_<experimentId>` in the
 * cache-override hook on every page the experiment is registered against (the five design-gallery
 * categories in SplitTestCategorySlug).
 *
 * Returns null when there is no cookie, which is how callers know the current page/category isn't
 * part of the experiment. The cookie is deliberately the only source, for the same reason
 * getBgRemoverUpsellVariant treats it as the only source in bg-remover-upsell-experiment.library.ts:
 * on the request that assigns the arm the hook has only queued a Set-Cookie header, so the PHP side
 * has no cookie to read yet either.
 */
export const getGallerySplitTestArm = (): number | null => {
  const arm = readCookie(SPLIT_TEST_COOKIE_PREFIX + WINDOWED_SCORE_GALLERY_AB_TEST_ID);

  if (arm === SPLIT_TEST_ARM_CONTROL || arm === SPLIT_TEST_ARM_WINDOWED) {
    return parseInt(arm, 10);
  }

  return null;
};

/**
 * Fires the click side of the poster/{seen,clickedFromGallery} split-test pair (see
 * GallerySplitTestRequestContext on the backend) plus a mirrored GA4 event, so the two data sources
 * can be cross-checked. No-ops unless the category is in the experiment and the visitor has an arm.
 *
 * Both conditions are load-bearing. The backend already ignores a beacon naming a category outside
 * the experiment, but GA4 has no such guard, so checking only the arm would leave GA4 recording
 * clicks across every gallery category while the database recorded five — two populations that
 * cannot be calibrated against each other, which is the whole reason the mirror exists. Checking the
 * category here also spares every non-experiment gallery page a beacon the server would discard.
 *
 * The two sides still aren't symmetric: the backend counter dedupes by session per bucket
 * (unique_engaged_sessions), but this GA4 event carries no such dedup, so a rapid repeat click sends
 * one GA4 event per click. Expect GA4's raw click count to run hotter than the DB's — that's expected
 * skew from this asymmetry, not a discrepancy to chase.
 */
export const trackGallerySplitTestTemplateClick = (categorySlug: string): void => {
  if (!TEST_CATEGORY_SLUGS.includes(categorySlug)) {
    return;
  }

  const arm = getGallerySplitTestArm();

  if (arm === null) {
    return;
  }

  sendTemplateClickBeacon(categorySlug);
  window.PMW.gtm.trackGA4CustomEvent(GALLERY_SPLIT_TEST_TEMPLATE_CLICK_GA4_EVENT_NAME, {categorySlug, arm});
};

/**
 * The click lands on an anchor that navigates to the template page, so the request has to survive
 * the unload. window.PMW.write is an abortable XHR behind an async CSRF-token fetch and routinely
 * never leaves the page; sendBeacon is queued by the browser and delivered regardless. The endpoint
 * is on Config\Security::$excludeUris for the same reason v/screenHeartbeat is: a beacon cannot
 * carry a token.
 */
const sendTemplateClickBeacon = (categorySlug: string): void => {
  const payload = new FormData();
  payload.append(TEMPLATE_CLICK_CATEGORY_SLUG_PARAM, categorySlug);
  navigator.sendBeacon(window.PMW.util.site_url(TEMPLATE_CLICK_ENDPOINT), payload);
};
